import {
  marketingParamsFromSearch,
  sanitizeMarketingUrl,
} from './marketingParams';

/**
 * Where this visit came from, banked at the arrival that started it and read at
 * `/signup/finalize` by {@link captureMarketingParams} for
 * `privateProfiles/{uid}.onboardingMarketingCapture` (→ Salesforce `website` /
 * `source_url__c`, → HubSpot campaign fields).
 *
 * **Why a record rather than reading the live URL.** The URL the account is
 * created on is the LAST hop of the visit, and a campaign's params routinely do
 * not reach it:
 *
 *   - the creator lands on `pop.store/?utm_source=…&utm_campaign=…&utm_id=…`
 *     and the link to `/signup` forwards only some of them,
 *   - Meta's mobile OAuth flow navigates the whole page to facebook.com and
 *     returns to a bare `/signup?code&state`,
 *   - the funnel's own hop to `/signup/finalize` is where the capture runs, so
 *     `location.pathname` there is never the landing page.
 *
 * **Why it is REPLACED, not accumulated.** A creator can land twice in one tab
 * from two different campaigns. Merging both would attribute the signup to a
 * mix of the two — params from a visit they already abandoned. So an arrival
 * that carries marketing params of its own REPLACES the record wholesale: the
 * newest campaign is the one that sent them, and the record holds exactly one
 * campaign's params, never a union of several.
 *
 * The store is `sessionStorage`, so it is per tab and dies with it — there is no
 * way for yesterday's campaign to reach today's signup.
 */
const kMarketingArrivalKey = 'signup.marketing.arrival';

/**
 * The href this DOCUMENT loaded on, read once when this module is evaluated.
 *
 * Evaluated at import time, which is before React renders anything and therefore
 * before the funnel starts rewriting its own URL — `useCreatorSocialSignup`
 * strips Meta's one-shot OAuth params from a mount effect, and child effects run
 * before their parent's, so nothing reading `location` from an effect can be
 * sure it is seeing the URL the visit arrived on.
 *
 * A module const, not a component ref: refs must not be read during render
 * (`react-hooks/refs`), and this value is a property of the document rather than
 * of any component — every surface that captures an arrival wants the same one.
 * It survives client-side navigation for the life of the document, which is
 * exactly the span an "arrival" covers.
 */
const kDocumentArrivalHref =
  typeof window === 'undefined' ? undefined : window.location.href;

/** @see kDocumentArrivalHref */
export function getDocumentArrivalHref(): string | undefined {
  return kDocumentArrivalHref;
}

export interface MarketingArrival {
  /** Href of the arrival that started this attribution, funnel params removed. */
  entryUrl: string;
  /** `document.referrer` at that arrival — the external page that linked here. */
  referrer?: string;
  /** Every marketing param that arrival carried, deduped, first value wins. */
  params: Record<string, string>;
}

/**
 * Bank the current arrival, if it is one. Called from the root layout on every
 * document load, and again on `/signup` mount to catch an in-app navigation
 * that carries a fresh campaign.
 *
 * Three outcomes, decided entirely by the URL — so the call is idempotent and
 * order-independent no matter how many surfaces make it:
 *
 *   1. Meta's OAuth return (`?code&state`, or `#access_token`) → NEVER recorded.
 *      It is the same visit continuing, and its params are a spent one-shot
 *      grant, not attribution. Recording it would drop the campaign that sent
 *      the creator and persist Meta's `code` as their entry URL.
 *   1b. The funnel's OWN hand-off to `/signup/finalize` → likewise never
 *      recorded. `useCreatorSocialSignup`'s fallback reaches finalize with a
 *      full document load, which is indistinguishable from a landing except by
 *      what it carries, and its params are the creator's Meta identity rather
 *      than a campaign.
 *   2. The URL carries params and looks like a CAMPAIGN → replace the record with
 *      THIS arrival. Params that are not attribution (`?plan=basic`, a checkout
 *      return) are recorded when nothing has been banked yet — they are the
 *      truthful entry — but never displace a campaign already on file.
 *   3. It carries none → keep what is there. An internal navigation, a reload
 *      or a cleaned URL continues the visit it belongs to; only the entry URL is
 *      backfilled if nothing had been recorded at all.
 *
 * @param href The arrival href. Defaults to {@link kDocumentArrivalHref}, the URL
 *   this document loaded on, which is the one callers want in every case that
 *   matters — the funnel rewrites `location` from its own mount effects.
 */
export function captureMarketingArrival(
  href?: string,
  referrer?: string,
): void {
  if (typeof window === 'undefined') return;
  const arrivalHref = href ?? kDocumentArrivalHref ?? window.location.href;
  if (isMetaOAuthReturnHref(arrivalHref)) return; // (1)
  if (isFunnelHandoffHref(arrivalHref)) return; // (1b)

  let search: string;
  try {
    search = new URL(arrivalHref).search;
  } catch {
    return; // not a parseable absolute href — nothing to attribute
  }
  const params = marketingParamsFromSearch(search);
  const existing = getMarketingArrival();

  if (Object.keys(params).length === 0) {
    // (3) Nothing to attribute to. Record the entry URL only if this is the
    // first thing we have seen, so a param-less landing still says where the
    // creator started.
    if (existing) return;
    writeMarketingArrival({
      entryUrl: sanitizeMarketingUrl(arrivalHref),
      referrer: externalReferrer(referrer),
      params: {},
    });
    return;
  }

  // (2) An existing record is only ever displaced by another CAMPAIGN. Plenty of
  // document loads carry params that are not attribution at all — `?plan=basic`
  // on a shared pricing link, a checkout return, a hard refresh of an app page —
  // and letting those replace the record would drop the campaign that sent the
  // creator and record `plan=basic` as their attribution.
  if (existing && !looksLikeCampaignArrival(params)) return;

  writeMarketingArrival({
    // Sanitized: an arrival can be a funnel URL of our own (the social hand-off
    // is a full document load), and the funnel's params have no business in a
    // recorded entry URL — see `sanitizeMarketingUrl`.
    entryUrl: sanitizeMarketingUrl(arrivalHref),
    referrer: externalReferrer(referrer),
    params,
  });
}

/** The banked arrival, or `undefined` if nothing was recorded this session. */
export function getMarketingArrival(): MarketingArrival | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = sessionStorage.getItem(kMarketingArrivalKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as MarketingArrival;
    // Anything that is not the shape we wrote is treated as absent rather than
    // trusted — this value reaches Salesforce.
    if (typeof parsed?.entryUrl !== 'string') return undefined;
    return { ...parsed, params: parsed.params ?? {} };
  } catch {
    return undefined;
  }
}

function writeMarketingArrival(arrival: MarketingArrival): void {
  try {
    sessionStorage.setItem(kMarketingArrivalKey, JSON.stringify(arrival));
  } catch {
    // sessionStorage throws in some privacy modes — attribution is best-effort.
  }
}

/**
 * `document.referrer`, but only when it is another site. A same-origin referrer
 * is our own previous page, which says nothing about where the visit came from
 * and would just repeat what `entryUrl` already records.
 */
function externalReferrer(referrer?: string): string | undefined {
  const value =
    referrer ?? (typeof document === 'undefined' ? '' : document.referrer);
  if (!value) return undefined;
  try {
    if (new URL(value).origin === window.location.origin) return undefined;
  } catch {
    return undefined;
  }
  return value;
}

/**
 * Non-`utm_` param names that mean "a campaign sent this visit": the ads click-ids
 * every network appends, and the funnel's own referral / store / coupon links.
 * Mirrors what `captureAnalyticsSessionParams` treats as marketing.
 *
 * This list decides ONLY whether an arrival may displace a campaign already on
 * file. It has no say in what gets captured — that stays "everything that is not
 * `kInternalFunnelParamKeys`", so a param a campaign invents next week is still
 * recorded in full; it just cannot, on its own, overwrite an earlier campaign.
 */
const kCampaignParamKeys: ReadonlySet<string> = new Set([
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'twclid',
  'clid_src',
  'msclkid',
  'ttclid',
  'r',
  'sid',
  'popstoreurl',
  'onboarding_coupon',
]);

/** @see kCampaignParamKeys */
function looksLikeCampaignArrival(params: Record<string, string>): boolean {
  return Object.keys(params).some(
    key => key.startsWith('utm_') || kCampaignParamKeys.has(key),
  );
}

/**
 * Is `href` the funnel handing off to its own finalize step, rather than a real
 * arrival? `socialFinalizeUrl` always sets `socialProvider`, and nothing else
 * does — no campaign links to `/signup/finalize` with a Meta identity attached.
 *
 * Checked structurally, and separately from `kInternalFunnelParamKeys`: that set
 * decides what gets CAPTURED (and its contents are deliberately unchanged),
 * while this decides whether an arrival is a new campaign at all. Without it, a
 * hand-off that carries no utm of its own reads as a fresh landing and replaces
 * the campaign that actually sent the creator.
 */
function isFunnelHandoffHref(href: string): boolean {
  try {
    return new URL(href).searchParams.has('socialProvider');
  } catch {
    return false;
  }
}

/**
 * Is `href` Meta's OAuth return rather than a real arrival? Instagram comes back
 * with `?code&state`, Facebook with `#access_token` in the fragment.
 *
 * Takes an href instead of reading `location` because that URL is rewritten in
 * the same commit that detects it: `useCreatorSocialSignup`'s mount effect
 * strips the one-shot params, and child effects run before their parent's. So
 * callers pass the href their component saw at render time, which is before any
 * of that.
 *
 * Matched structurally rather than against `kMetaOAuthState` so this module
 * stays free of auth imports — the only question here is "did a campaign send
 * this URL, or did Meta?".
 */
export function isMetaOAuthReturnHref(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  return Boolean(
    (url.searchParams.get('code') && url.searchParams.get('state')) ||
    hash.get('access_token'),
  );
}

/**
 * Put the banked arrival's params back onto a query string, for the params the
 * funnel READS rather than records: `utm_regintent` decides whether the niche
 * question is asked, `utm_eventname` reaches event mode, `popstoreurl` and
 * `onboarding_coupon` are consumed downstream. Meta's mobile OAuth return drops
 * all of them, so behaviour — not just attribution — needs them back.
 *
 * Live params always win and are never touched, and a key already present is
 * never appended a second time, so this cannot duplicate a param or resurrect
 * an older value over the current one.
 */
export function restoreMarketingParams(
  params: URLSearchParams,
): URLSearchParams {
  const arrival = getMarketingArrival();
  if (!arrival) return params;
  for (const [key, value] of Object.entries(arrival.params)) {
    if (params.has(key)) continue;
    params.append(key, value);
  }
  return params;
}
